import { HttpClient } from '@angular/common/http';
import { AnimationCallbackEvent, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NAVIGATE } from '../../core/navigate';
import { RegistrationFormsService } from '../forms/registration-forms.service';
import { Step1 } from '../step1/step1';
import { Step2 } from '../step2/step2';
import { Thankyou } from '../thankyou/thankyou';

export type RegistrationStep = 'step1' | 'step2' | 'thankyou';

export interface Country {
  code: string;
  name: string;
  defaultCurrency: string;
}
export interface TimeZone {
  key: string;
  value: string;
}

const REDIRECT_DELAY_MS = 2000;
// a hanging API must not keep step 2 on the spinner; without the list the currency falls back to USD
const CURRENCIES_TIMEOUT_MS = 5000;

@Component({
  selector: 'app-register',
  imports: [Step1, Step2, Thankyou],
  templateUrl: './register.html',
  styleUrl: './register.scss',
})
export class Register {
  private readonly forms = inject(RegistrationFormsService);
  private readonly navigate = inject(NAVIGATE);
  private readonly http = inject(HttpClient);

  protected readonly step = signal<RegistrationStep>('step1');
  protected readonly direction = signal<'forward' | 'back'>('forward');

  // step 2 waits for these; a failed request degrades that one list, the form must still open
  protected readonly data = toSignal(
    forkJoin({
      countries: this.http.get<Country[]>('assets/countries.json').pipe(catchError(() => of([] as Country[]))),
      timeZones: this.http
        .get<Record<string, TimeZone[]>>('assets/country2timezone.json')
        .pipe(catchError(() => of({} as Record<string, TimeZone[]>))),
      currencies: this.http.get<string[]>(`${environment.API_ENDPOINT_V2}/currencies`).pipe(
        timeout(CURRENCIES_TIMEOUT_MS),
        catchError(() => of([] as string[])),
      ),
    }),
  );

  onNext() {
    if (this.forms.step1.valid) {
      this.direction.set('forward');
      this.step.set('step2');
    } else {
      this.forms.markStepTouched(1);
    }
  }

  onBack() {
    this.direction.set('back');
    this.step.set('step1');
  }

  onSubmitted(redirect: string) {
    this.direction.set('forward');
    this.step.set('thankyou');
    setTimeout(() => this.navigate(redirect), REDIRECT_DELAY_MS);
  }

  /** Runs the keyframes of `className` on the entering/leaving step; no class means no animation. */
  protected animate(event: AnimationCallbackEvent, className: string) {
    if (!className) {
      event.animationComplete();
      return;
    }
    event.target.classList.add(className);
    const onEnd = (e: Event) => {
      // animationend bubbles: a child's animation (an error line fading in) must not end the step transition
      if (e.target !== event.target) {
        return;
      }
      event.target.removeEventListener('animationend', onEnd);
      event.target.classList.remove(className);
      event.animationComplete();
    };
    event.target.addEventListener('animationend', onEnd);
  }
}
